import React, {useEffect, useState} from 'react'
import {usePage} from '@salesforce/commerce-sdk-react'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
// Components
import {StackProps, TextProps, VStack, useMultiStyleConfig} from '@chakra-ui/react'
import {Widget} from '_uiComponents'
// Constants
import {MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '_utils/data/constants'
//Hooks
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useAnalytics, useCurrentBasket, usePageDesigner, useHeaderSize} from '_customHooks'
import {useGlobalConfigState, useHeaderDispatch} from '_context'
import {MetaTags} from '_utils'
import {useQuery} from '@tanstack/react-query'
import {useIntl} from 'react-intl'

const Home = () => {
    let isFirstLoad = false
    const PAGE_DESIGNER_PAGE_ID = 'homepage'

    const {res} = useServerContext()
    if (res) {
        res.set('Cache-Control', `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`)
    }

    const {analyticsEventInit, analyticsEventPwaPage} = useAnalytics()
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const {locale} = useMultiSite()
    const {resetPageDesignerPosition, renderPageDesignerRegion} = usePageDesigner()
    const {headerSize, accessibilityBannerSize} = useHeaderSize(false)
    const einstein = useEinstein()
    const {appPreferences} = useGlobalConfigState()
    const {setTransparentMode} = useHeaderDispatch()
    const {disableEditorialWidget} = appPreferences
    const {formatMessage} = useIntl()

    const [windowOrigin, setWindowOrigin] = useState<{protocol: string; host: string; pathname: string} | null>(null)

    const styles = useMultiStyleConfig('HomePage', {headerSize, accessibilityBannerSize}) as Record<string, StackProps & TextProps>

    const {data: homepagePD} = usePage({parameters: {pageId: PAGE_DESIGNER_PAGE_ID}})
    resetPageDesignerPosition()

    const getSeoUrls = async () => {
        const baseUrl = `${window.location.protocol}//${window.location.host}`
        const queryParams = new URLSearchParams()
        queryParams.append('locale', locale.alias)
        queryParams.append('host', window.location.host)

        const response = await fetch(`${baseUrl}/getSeoUrls/?${queryParams}`, {
            method: 'GET',
        })

        if (!response.ok) {
            return []
        }

        const responseJson = await response.json()
        return responseJson || []
    }

    const {data: seoUrls} = useQuery(['seoUrls'], getSeoUrls, {enabled: typeof window !== 'undefined'})

    const metaProps = {
        title: formatMessage({id: 'seo.home.title', defaultMessage: 'Brunello Cucinelli: Official boutique for women, men and kids'}),
        description: formatMessage({
            id: 'seo.home.description',
            defaultMessage:
                'Discover Brunello Cucinelli collections on the official online boutique. Find out latest arrivals and get inspired by our looks for women, men and kids.',
        }),
        keywords: formatMessage({id: 'seo.home.keywords', defaultMessage: 'Brunello Cucinelli - official boutique'}),
        jsonLd: JSON.stringify({
            '@context': 'http://schema.org/',
            '@type': 'Organization',
            url: windowOrigin ? `${windowOrigin.protocol}//${windowOrigin.host}${windowOrigin.pathname}` : '',
            logo: windowOrigin ? `${windowOrigin.protocol}//${windowOrigin.host}/logo.svg` : '',
        }),
        alternateUrls: seoUrls?.alternate ?? [],
        canonicalUrl: seoUrls?.canonical ?? '',
    }

    /**
     * Triggers `analyticsEventPwaPage` when data layer is already initialized.
     * @remarks
     * The data layer check ensures that the event will be triggered
     * only between PWA-pages navigation.
     */
    const runPwaPageEvent = () => {
        if (window && window.dataLayer) {
            analyticsEventPwaPage({
                pageCategory: 'homepage',
                pagesubCategory: '',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: '',
            })
        }
    }

    useEffect(() => {
        if (customer && locale && basket && !isFirstLoad) {
            analyticsEventInit({
                country: locale.id.split('-')[1],
                currency: basket.currency,
                pageCategory: 'homepage',
                pageSubCategory: 'neutral',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: '',
            })
            isFirstLoad = true
        }
    }, [customer, locale, basket])

    useEffect(() => {
        setWindowOrigin({
            protocol: window.location.protocol,
            host: window.location.host,
            pathname: window.location.pathname,
        })

        einstein.sendViewPage(window.location.href)
        runPwaPageEvent()
    }, [])

    // useEffect(() => {
    //     if (homepagePD?.typeId === 'fullPage') {
    //         setTransparentMode(false)
    //     }
    // }, [homepagePD])

    return (
        <VStack {...styles.container} data-testid="homepage">
            <MetaTags {...metaProps} />

            {homepagePD && homepagePD.regions?.map((region, idx) => renderPageDesignerRegion({region, isMainBanner: idx === 0}))}

            {(disableEditorialWidget === null ||
                (typeof disableEditorialWidget !== 'undefined' && disableEditorialWidget !== null && disableEditorialWidget.indexOf('HOMEPAGE') === -1)) && (
                <Widget />
            )}
        </VStack>
    )
}

Home.getTemplateName = () => 'home'

export default Home
